// World: entity store, spatial index, player state and the fixed-timestep tick
// pipeline. This module is the contract every system module builds against.
//
// ENTITY SHAPE (plain objects; no classes, no prototypes -- keep them monomorphic)
//   id        number, unique, never reused
//   kind      'unit' | 'building' | 'resource'
//   defId     key into UnitDefs / BuildingDefs / ResourceDefs
//   owner     0 | 1, or -1 for resources
//   x, y      float tile coordinates of the entity CENTRE
//   tx, ty    (buildings/resources only) integer anchor of the footprint
//   w, h      (buildings/resources only) footprint size in tiles
//   hp, maxHp, armor
//   radius, speed, sight, flying, detector
//   alive     boolean; false entities are reaped at end of tick
//   order     current order object, or null   { type, x, y, targetId, defId, ... }
//   orderQueue array of pending orders (shift-queue)
//   -- movement fields (owned by movement.js) --
//   vx, vy, facing, path, pathGoal, stuckTicks, moveState
//   -- combat fields (owned by combat.js) --
//   weaponCd, target, sieged, siegeTimer, cloaked, detected
//   -- economy fields (owned by economy.js) --
//   carrying {type, amount} | null, gatherNode, gatherTimer, dropoffId
//   buildProgress, buildTime, constructing, builderId
//   queue [] production queue entries { kind:'unit'|'research', defId, timeLeft, total }
//   rally { x, y, targetId } | null
//
// SYSTEM CONTRACT: each system exports `xxxSystem(world)` and is called once per
// tick in the order defined in step(). Systems must only mutate the fields they
// own (listed above) plus emit events. Randomness must come from world.rng.

import { RNG } from './rng.js';
import { buildMap } from './map.js';
import { UnitDefs, BuildingDefs, ResourceDefs } from './defs.js';
import {
  TICK_DT, MAX_SUPPLY, START_MINERALS, START_GAS, START_WORKERS,
  PATCH_AMOUNT, GEYSER_AMOUNT,
} from './constants.js';

import { applyCommand } from './commands.js';
import { movementSystem, onGridChanged } from './movement.js';
import { combatSystem } from './combat.js';
import { economySystem } from './economy.js';
import { fogSystem, createFog } from './fog.js';
import { aiSystem, createAI } from './ai.js';

const CELL = 4; // spatial hash cell size, tiles
// Roughly a second of very busy combat. The renderer drains every frame; this
// only bites when nobody is listening.
const MAX_EVENT_BUFFER = 4096;

export class World {
  constructor({ seed = 1337, aiEnabled = true, aiDifficulty = 'standard' } = {}) {
    this.rng = new RNG(seed);
    this.tick = 0;
    this.time = 0;              // game seconds
    this.gameOver = null;       // { winner, reason }

    const map = buildMap();
    this.grid = map.grid;
    this.bases = map.bases;
    this.startLocations = map.startLocations;

    this.entities = new Map();
    this.nextId = 1;
    this.byPlayer = [new Set(), new Set()];
    this.resources = new Set();
    this.pending = [];          // commands queued for the next tick
    this.events = [];

    this.players = [0, 1].map((id) => ({
      id,
      minerals: START_MINERALS,
      gas: START_GAS,
      supplyUsed: 0,
      supplyMax: 0,
      tech: new Set(),
      isAI: id === 1 && aiEnabled,
      defeated: false,
      stats: { mineralsMined: 0, gasMined: 0, unitsLost: 0, unitsKilled: 0, unitsBuilt: 0, supplyBlockedTicks: 0 },
    }));

    // spatial hash
    this.cw = Math.ceil(this.grid.w / CELL);
    this.ch = Math.ceil(this.grid.h / CELL);
    this.cells = Array.from({ length: this.cw * this.ch }, () => []);

    this.fog = [createFog(this, 0), createFog(this, 1)];
    this.ai = this.players[1].isAI ? createAI(this, 1, aiDifficulty) : null;

    this._spawnMapResources(map.nodes);
    this._spawnStartingForces();
    this.reindex();
    this._initSupply();
    fogSystem(this);
  }

  // ---- entity lifecycle --------------------------------------------------
  _spawnMapResources(nodes) {
    for (const n of nodes) {
      const def = ResourceDefs[n.type];
      const e = {
        id: this.nextId++, kind: 'resource', defId: n.type, owner: -1,
        tx: n.x, ty: n.y, w: n.w, h: n.h,
        x: n.x + n.w / 2, y: n.y + n.h / 2,
        radius: def.radius, base: n.base,
        amount: n.type === 'mineral' ? PATCH_AMOUNT : GEYSER_AMOUNT,
        hp: 1, maxHp: 1, alive: true, gatherers: 0, refineryId: null,
      };
      this.entities.set(e.id, e);
      this.resources.add(e.id);
      this.grid.stamp(e.tx, e.ty, e.w, e.h, e.id);
      this.grid.markNoBuild(e.tx, e.ty, e.w, e.h, 1);
    }
  }

  _spawnStartingForces() {
    for (let p = 0; p < 2; p++) {
      const s = this.startLocations[p];
      const cc = this.spawnBuilding(p, 'command_center', s.x - 2, s.y - 2, { complete: true });
      const spread = [[3.2, 2.6], [3.2, -2.6], [-3.2, 2.6], [-3.2, -2.6], [0, 3.6], [0, -3.6]];
      for (let i = 0; i < START_WORKERS; i++) {
        const [dx, dy] = spread[i % spread.length];
        const u = this.spawnUnit(p, 'worker', s.x + dx, s.y + dy);
        u.order = { type: 'gather', targetId: null };  // economy picks the patch
      }
      cc.rally = null;
    }
  }

  /**
   * Supply is derived by economySystem each tick, which means it reads 0/0
   * until the first step. Seed it at construction so the HUD's very first frame
   * and any command issued before tick 1 both see the true opening supply.
   */
  _initSupply() {
    for (let p = 0; p < 2; p++) {
      let used = 0, max = 0;
      for (const id of this.byPlayer[p]) {
        const e = this.entities.get(id);
        if (!e || !e.alive) continue;
        if (e.kind === 'unit') used += UnitDefs[e.defId].supply;
        else if (e.kind === 'building' && !e.constructing) max += BuildingDefs[e.defId].supplyProvided || 0;
      }
      this.players[p].supplyUsed = used;
      this.players[p].supplyMax = Math.min(MAX_SUPPLY, max);
    }
  }

  spawnUnit(owner, defId, x, y) {
    const d = UnitDefs[defId];
    const e = {
      id: this.nextId++, kind: 'unit', defId, owner,
      x, y, hp: d.hp, maxHp: d.hp, armor: d.armor,
      radius: d.radius, speed: d.speed, sight: d.sight,
      flying: !!d.flying, detector: !!d.detector, size: d.size,
      alive: true, spawnTick: this.tick,
      order: null, orderQueue: [],
      vx: 0, vy: 0, facing: owner === 0 ? -Math.PI / 4 : (3 * Math.PI) / 4,
      path: null, pathGoal: null, stuckTicks: 0, moveState: 'idle',
      weaponCd: 0, target: null, sieged: false, siegeTimer: 0,
      cloaked: false, detected: true,
      // Combat-owned optionals, seeded here so entities stay monomorphic.
      // The two "long ago" sentinels must be finite, not undefined, or the
      // first comparison against them is NaN and silently false.
      postX: undefined, postY: undefined, leashing: false,
      noAcqId: 0, noAcqUntil: -1e6, siegePending: false,
      lastDamagedTick: -1e6, lastAttackerId: null,
      carrying: null, gatherNode: null, gatherTimer: 0, gatherState: 'to_node', dropoffId: null,
      buildTargetId: null, moveTarget: null,
    };
    this.entities.set(e.id, e);
    this.byPlayer[owner].add(e.id);
    this.players[owner].stats.unitsBuilt++;
    this.emit({ type: 'spawn', id: e.id, owner, defId, x, y });
    return e;
  }

  spawnBuilding(owner, defId, tx, ty, { complete = false, builderId = null } = {}) {
    const d = BuildingDefs[defId];
    const e = {
      id: this.nextId++, kind: 'building', defId, owner,
      tx, ty, w: d.size, h: d.size,
      x: tx + d.size / 2, y: ty + d.size / 2,
      hp: complete ? d.hp : Math.max(1, Math.floor(d.hp * 0.1)),
      maxHp: d.hp, armor: d.armor, radius: d.size / 2,
      sight: d.sight, flying: false, detector: !!d.detector, size: 2,
      alive: true, spawnTick: this.tick,
      order: null, orderQueue: [],
      constructing: !complete, buildProgress: complete ? 1 : 0, buildTime: d.buildTime,
      builderId,
      queue: [], rally: null,
      weaponCd: 0, target: null, cloaked: false, detected: true,
      lastDamagedTick: -1e6, lastAttackerId: null, cancelled: false,
      geyserId: null, gasWorkers: 0,
    };
    this.entities.set(e.id, e);
    this.byPlayer[owner].add(e.id);
    this.grid.stamp(tx, ty, e.w, e.h, e.id);
    onGridChanged(this);
    this.emit({ type: 'build_start', id: e.id, owner, defId, x: e.x, y: e.y });
    return e;
  }

  removeEntity(id) {
    const e = this.entities.get(id);
    if (!e) return;
    e.alive = false;
    if (e.kind !== 'resource' && e.owner >= 0) this.byPlayer[e.owner].delete(id);
    if (e.kind === 'resource') this.resources.delete(id);
    if (e.tx !== undefined) { this.grid.unstamp(e.tx, e.ty, e.w, e.h); onGridChanged(this); }
    this.entities.delete(id);
  }

  get(id) { return id == null ? null : this.entities.get(id) || null; }
  alive(id) { const e = this.entities.get(id); return e && e.alive ? e : null; }

  // ---- spatial index -----------------------------------------------------
  reindex() {
    for (const c of this.cells) c.length = 0;
    for (const e of this.entities.values()) {
      if (!e.alive) continue;
      const cx = Math.min(this.cw - 1, Math.max(0, (e.x / CELL) | 0));
      const cy = Math.min(this.ch - 1, Math.max(0, (e.y / CELL) | 0));
      this.cells[cy * this.cw + cx].push(e);
    }
  }

  /** All live entities whose centre is within `r` tiles of (x,y). */
  queryRadius(x, y, r, out = []) {
    out.length = 0;
    const x0 = Math.max(0, ((x - r) / CELL) | 0), x1 = Math.min(this.cw - 1, ((x + r) / CELL) | 0);
    const y0 = Math.max(0, ((y - r) / CELL) | 0), y1 = Math.min(this.ch - 1, ((y + r) / CELL) | 0);
    const r2 = r * r;
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const cell = this.cells[cy * this.cw + cx];
        for (let i = 0; i < cell.length; i++) {
          const e = cell[i];
          const dx = e.x - x, dy = e.y - y;
          if (dx * dx + dy * dy <= r2) out.push(e);
        }
      }
    }
    return out;
  }

  unitsOf(player) {
    const out = [];
    for (const id of this.byPlayer[player]) { const e = this.entities.get(id); if (e && e.alive && e.kind === 'unit') out.push(e); }
    return out;
  }
  buildingsOf(player) {
    const out = [];
    for (const id of this.byPlayer[player]) { const e = this.entities.get(id); if (e && e.alive && e.kind === 'building') out.push(e); }
    return out;
  }
  /** Completed buildings of `defId` owned by `player`. */
  hasBuilding(player, defId) {
    for (const id of this.byPlayer[player]) {
      const e = this.entities.get(id);
      if (e && e.alive && e.kind === 'building' && e.defId === defId && !e.constructing) return true;
    }
    return false;
  }

  // ---- commands ----------------------------------------------------------
  /** Deterministic path: the command lands at the start of the next tick. */
  issue(cmd) { this.pending.push(cmd); }
  /**
   * Zero-latency path for LOCAL player input only. The simulation runs at 24 Hz
   * but the display runs at 60+, so a command routed through the pending queue
   * waits up to 42 ms before anything visibly happens -- which is exactly the
   * "mushy" feel to avoid. Input applies immediately instead; the AI and the
   * headless harness keep using issue() so replays stay reproducible.
   */
  issueNow(cmd) { applyCommand(this, cmd); }

  // ---- events ------------------------------------------------------------
  /**
   * Record an event for the view layer.
   *
   * The renderer drains this every frame, so in normal play it holds one
   * frame's worth. Nothing drains it in a headless simulation, though, and an
   * unbounded queue of shots and damage ticks is a genuine leak: a blind
   * playtester ran a long session and killed the Node process at its 2 GB heap
   * limit. Capping it costs nothing in the browser and makes a long headless
   * match safe.
   */
  emit(ev) {
    ev.tick = this.tick;
    const q = this.events;
    if (q.length >= MAX_EVENT_BUFFER) { q.splice(0, q.length - MAX_EVENT_BUFFER + 1); this.eventsDropped = (this.eventsDropped || 0) + 1; }
    q.push(ev);
  }
  /**
   * Take the frame's events. Exactly one consumer may call this, because the
   * second caller gets an empty array -- which is a silent, order-dependent
   * bug rather than a loud one.
   */
  drainEvents() { const e = this.events; this.events = []; return e; }
  /** Read the current batch WITHOUT consuming it, so several views can observe it. */
  peekEvents() { return this.events; }

  // ---- tick --------------------------------------------------------------
  step() {
    if (this.gameOver) return;
    this.tick++;
    this.time = this.tick * TICK_DT;

    // Snapshot positions so the renderer can interpolate between logic frames.
    // Without this a 24 Hz simulation looks like 24 fps no matter how fast the
    // display is running.
    for (const e of this.entities.values()) { if (e.kind === 'unit') { e.px = e.x; e.py = e.y; e.pfacing = e.facing; } }

    for (const cmd of this.pending) applyCommand(this, cmd);
    this.pending.length = 0;

    this.reindex();
    aiSystem(this);
    economySystem(this);
    combatSystem(this);
    movementSystem(this);
    fogSystem(this);
    this.reap();
    this.checkVictory();
  }

  reap() {
    let dead = null;
    for (const e of this.entities.values()) {
      if (e.hp <= 0 && e.alive && e.kind !== 'resource') (dead ||= []).push(e);
      if (e.kind === 'resource' && e.amount <= 0 && e.defId === 'mineral') (dead ||= []).push(e);
    }
    if (!dead) return;
    for (const e of dead) {
      this.emit({ type: 'death', id: e.id, owner: e.owner, kind: e.kind, defId: e.defId, x: e.x, y: e.y });
      if (e.owner >= 0) this.players[e.owner].stats.unitsLost++;
      this.removeEntity(e.id);
    }
  }

  checkVictory() {
    for (let p = 0; p < 2; p++) {
      if (this.players[p].defeated) continue;
      let any = false;
      for (const id of this.byPlayer[p]) { const e = this.entities.get(id); if (e && e.alive) { any = true; break; } }
      if (!any) {
        this.players[p].defeated = true;
        this.gameOver = { winner: 1 - p, loser: p, reason: 'eliminated', tick: this.tick };
        this.emit({ type: 'game_over', ...this.gameOver });
      }
    }
  }
}

export const supplyCap = (p) => Math.min(MAX_SUPPLY, p.supplyMax);

/**
 * Distance from a point to an entity's OUTER EDGE -- its tile footprint for
 * buildings and resources, its collision circle for units.
 *
 * Interaction ranges must be measured this way. Measuring to a 4x4 building's
 * centre instead means a worker touching the north face is 2.0 tiles away while
 * one touching a corner is 3.5, so any single radial threshold either rejects
 * legitimate contact or accepts contact from thin air.
 */
export function edgeDist(e, x, y) {
  if (e.tx !== undefined) {
    const dx = Math.max(e.tx - x, 0, x - (e.tx + e.w));
    const dy = Math.max(e.ty - y, 0, y - (e.ty + e.h));
    return Math.hypot(dx, dy);
  }
  return Math.max(0, Math.hypot(e.x - x, e.y - y) - (e.radius || 0));
}

/** Radial stop distance that puts a unit in contact with `e` from any angle. */
export function contactRadius(e, unitRadius) {
  const half = e.tx !== undefined ? Math.max(e.w, e.h) / 2 : (e.radius || 0);
  return half + unitRadius + 0.25;
}
